import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

const ProfitAnalysis = () => {
  const [view, setView] = useState('overall');
  const [data, setData] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [topCount, setTopCount] = useState(10);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => { fetchMonthlyTrend(); }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const resp = await axios.get(`${API}/reports/profit-analysis`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { from_date: fromDate, to_date: toDate, view, top_count: topCount }
      });
      setData(resp.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const fetchMonthlyTrend = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const resp = await axios.get(`${API}/reports/profit-monthly`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonthlyData(resp.data.months || []);
    } catch (e) { console.error(e); }
  };

  const formatCurrency = (v) => `₹${(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const exportPDF = () => {
    if (!data) return;
    const pdf = new jsPDF('l', 'mm', 'a4');
    pdf.setFontSize(16); pdf.setFont('helvetica', 'bold');
    pdf.text('Profit Analysis Report', 14, 15);
    pdf.setFontSize(9); pdf.setFont('helvetica', 'normal');
    pdf.text(`Period: ${fromDate} to ${toDate} | View: ${view}`, 14, 22);
    pdf.text(`Revenue: Rs.${data.summary.total_revenue.toFixed(2)} | Cost: Rs.${data.summary.total_cost.toFixed(2)} | Gross Profit: Rs.${data.summary.gross_profit.toFixed(2)} | Expenses: Rs.${data.summary.total_expenses.toFixed(2)} | Net Profit: Rs.${data.summary.net_profit.toFixed(2)} | Margin: ${data.summary.profit_margin_percent}%`, 14, 28);

    const tableData = (data.data || data.top_products || []).map((r, i) => [
      i + 1,
      r.product_name || r.category_name || r.brand_name,
      r.qty_sold,
      r.revenue.toFixed(2),
      r.cost.toFixed(2),
      r.profit.toFixed(2),
      `${r.margin_percent}%`
    ]);
    autoTable(pdf, {
      startY: 33,
      head: [['#', view === 'category' ? 'Category' : view === 'brand' ? 'Brand' : 'Product', 'Qty', 'Revenue', 'Cost', 'Profit', 'Margin']],
      body: tableData,
      theme: 'grid', headStyles: { fillColor: [79, 70, 229], fontSize: 8 }, bodyStyles: { fontSize: 8 },
      margin: { left: 14, right: 14 }
    });
    pdf.save(`Profit_Analysis_${view}_${fromDate}_to_${toDate}.pdf`);
  };

  const SummaryCard = ({ label, value, color, sub }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`text-xl font-bold mt-1 ${color}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );

  return (
    <div data-testid="profit-analysis-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Profit Analysis</h1>
          <p className="text-slate-500">Revenue, costs, margins & trends</p>
        </div>
        {data && (
          <button onClick={exportPDF} className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700" data-testid="profit-export-pdf">Export PDF</button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">View</label>
            <select value={view} onChange={e => setView(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-medium" data-testid="profit-view-select">
              <option value="overall">Overall</option>
              <option value="product">Product-wise</option>
              <option value="category">Category-wise</option>
              <option value="brand">Brand-wise</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">From</label>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">To</label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Top N</label>
            <input type="number" value={topCount} onChange={e => setTopCount(parseInt(e.target.value) || 10)} min={3} max={100} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="profit-top-count" />
          </div>
          <div className="col-span-2 flex items-end">
            <button onClick={fetchReport} disabled={loading} className="w-full px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 text-sm font-medium" data-testid="profit-generate-btn">
              {loading ? 'Loading...' : 'Generate Report'}
            </button>
          </div>
        </div>
      </div>

      {/* Monthly Profit Trend Graph */}
      {monthlyData.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">12-Month Profit Trend</h2>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={monthlyData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month_short" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => [`₹${v.toLocaleString('en-IN')}`, '']} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="revenue" name="Revenue" fill="#6366f1" radius={[3, 3, 0, 0]} />
              <Bar dataKey="expenses" name="Expenses" fill="#f59e0b" radius={[3, 3, 0, 0]} />
              <Bar dataKey="net_profit" name="Net Profit" fill="#10b981" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Report Data */}
      {data && (
        <div className="space-y-5">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <SummaryCard label="Total Revenue" value={formatCurrency(data.summary.total_revenue)} color="text-slate-800" sub={`${data.summary.total_bills} bills`} />
            <SummaryCard label="Total Cost (COGS)" value={formatCurrency(data.summary.total_cost)} color="text-amber-700" />
            <SummaryCard label="Gross Profit" value={formatCurrency(data.summary.gross_profit)} color="text-blue-700" sub={`${data.summary.gross_margin_percent}% margin`} />
            <SummaryCard label="Expenses" value={formatCurrency(data.summary.total_expenses)} color="text-red-600" />
            <SummaryCard label="Net Profit" value={formatCurrency(data.summary.net_profit)} color={data.summary.net_profit >= 0 ? 'text-emerald-700' : 'text-red-700'} sub={`${data.summary.profit_margin_percent}% margin`} />
          </div>

          {/* Profit Margin Bar */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-slate-600">Profit Margin</span>
              <span className={`text-lg font-bold ${data.summary.profit_margin_percent >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{data.summary.profit_margin_percent}%</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-3">
              <div className={`h-3 rounded-full transition-all ${data.summary.profit_margin_percent >= 20 ? 'bg-emerald-500' : data.summary.profit_margin_percent >= 10 ? 'bg-amber-500' : 'bg-red-500'}`}
                style={{ width: `${Math.min(Math.max(data.summary.profit_margin_percent, 0), 100)}%` }}></div>
            </div>
          </div>

          {/* Overall view - Expense breakdown pie + top products */}
          {view === 'overall' && data.expense_breakdown && Object.keys(data.expense_breakdown).length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Expense Breakdown</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={Object.entries(data.expense_breakdown).map(([k, v]) => ({ name: k, value: v }))} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                      {Object.keys(data.expense_breakdown).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={v => formatCurrency(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Top {topCount} Profitable Products</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={(data.top_products || []).slice(0, 8)} layout="vertical" margin={{ left: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                    <YAxis type="category" dataKey="product_name" tick={{ fontSize: 9 }} width={60} />
                    <Tooltip formatter={v => formatCurrency(v)} />
                    <Bar dataKey="profit" fill="#10b981" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Data Table */}
          {(data.data || data.top_products) && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-3 text-left font-medium text-slate-600">#</th>
                    <th className="px-3 py-3 text-left font-medium text-slate-600">{view === 'category' ? 'Category' : view === 'brand' ? 'Brand' : 'Product'}</th>
                    <th className="px-3 py-3 text-right font-medium text-slate-600">Qty Sold</th>
                    <th className="px-3 py-3 text-right font-medium text-slate-600">Revenue</th>
                    <th className="px-3 py-3 text-right font-medium text-slate-600">Cost</th>
                    <th className="px-3 py-3 text-right font-medium text-slate-600">Profit</th>
                    <th className="px-3 py-3 text-right font-medium text-slate-600">Margin %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(data.data || data.top_products || []).map((row, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-3 py-2.5">{i + 1}</td>
                      <td className="px-3 py-2.5 font-medium">{row.product_name || row.category_name || row.brand_name}</td>
                      <td className="px-3 py-2.5 text-right">{row.qty_sold}</td>
                      <td className="px-3 py-2.5 text-right">{formatCurrency(row.revenue)}</td>
                      <td className="px-3 py-2.5 text-right text-amber-600">{formatCurrency(row.cost)}</td>
                      <td className={`px-3 py-2.5 text-right font-bold ${row.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(row.profit)}</td>
                      <td className={`px-3 py-2.5 text-right font-medium ${row.margin_percent >= 20 ? 'text-emerald-600' : row.margin_percent >= 10 ? 'text-amber-600' : 'text-red-600'}`}>{row.margin_percent}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(data.data || data.top_products || []).length === 0 && (
                <p className="text-center py-8 text-slate-400">No profit data for this period</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfitAnalysis;
